import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { listMilestones, reachMilestone, type Milestone } from "../api/delivery.ts";

const STATUS_COLORS: Record<string, string> = {
  PENDING: "bg-gray-700 text-gray-300",
  IN_PROGRESS: "bg-yellow-900/50 text-yellow-300",
  REACHED: "bg-green-900/50 text-green-300",
  MISSED: "bg-red-900/30 text-red-400",
};

export function DeliveryMilestonesPage() {
  const { token } = useAuth();
  const [items, setItems] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<string | null>(null);

  const load = () => {
    if (token == null) return;
    setLoading(true);
    listMilestones(token)
      .then(setItems)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [token]);

  const reach = async (ms: Milestone) => {
    if (token == null) return;
    try {
      setActing(ms.id);
      await reachMilestone(token, ms.id, ms.version);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setActing(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-white">Milestones</h1>
        {loading && <p className="text-gray-400">Loading…</p>}
        {error != null && <p className="text-red-400">{error}</p>}

        <div className="space-y-3">
          {items.map((ms) => (
            <div key={ms.id} className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-white font-medium">{ms.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[ms.status] ?? "bg-gray-700 text-gray-300"}`}>{ms.status}</span>
                  </div>
                  {ms.description && <p className="text-gray-400 text-sm mt-1">{ms.description}</p>}
                  <div className="flex gap-4 mt-1 text-xs text-gray-500">
                    {ms.targetDate && <span>Target: {ms.targetDate.split("T")[0]}</span>}
                    {ms.reachedDate && <span className="text-green-400">Reached: {ms.reachedDate.split("T")[0]}</span>}
                  </div>
                </div>
                {(ms.status === "PENDING" || ms.status === "IN_PROGRESS") && (
                  <button disabled={acting != null} onClick={() => reach(ms)} className="px-3 py-1 text-xs bg-green-700 hover:bg-green-600 text-white rounded transition-colors disabled:opacity-50">Mark Reached</button>
                )}
              </div>
            </div>
          ))}
          {items.length === 0 && !loading && <p className="text-gray-400 text-sm">No milestones yet.</p>}
        </div>
      </div>
    </Layout>
  );
}
